import { useState } from 'react'
import { useOutletContext } from 'react-router'
import { PageFooter } from '@/components/commons/page-footer'
import { PageHero } from '@/components/commons/page-hero'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { ArrowRightIcon } from '@/components/ui/icons'
import { PhotosetSpaceElement } from '@/features/photoset/components/photoset-space-element'
import {
	PhotosetStepBodyContainer,
	PhotosetStepWrapper
} from '@/features/photoset/components/photoset-step-atoms'
import { StepHeroControls } from '@/features/photoset/components/step-hero-controls'
import { VettingQaConfirmationModal } from '@/features/photoset/components/vetting-qa-confirmation-modal/vetting-qa-confirmation-modal'
import { VettingQaSpaceImages } from '@/features/photoset/components/vetting-qa-space-images'
import { useCurrentPhotosetSubRoute } from '@/features/photoset/hooks/use-current-photoset-sub-route'
import { GET_PHOTOSET_IMAGES_LIST_RESPONSE_TYPE } from '@/features/photoset/queries/use-get-photoset-images-list'
import { useGetPhotosetSpaces } from '@/features/photoset/queries/use-get-photoset-spaces'
import type { PhotosetURLParams } from '@/features/photoset/types'
import { useTranslation } from '@/translations/i18n'

export const PhotosetVettingQaStep = () => {
	const { t } = useTranslation()
	const { urlParams } = useOutletContext<{ urlParams: PhotosetURLParams }>()
	const currentPhotosetSubRoute = useCurrentPhotosetSubRoute()

	//statuses
	const [showConfirmationModal, setShowConfirmationModal] = useState(false)

	// queries
	const {
		data: photosetSpaces,
		isFetching: isFetchingManualRetouchingSpaces,
		isError: isErrorManualRetouchingSpaces
	} = useGetPhotosetSpaces({
		urlParams: {
			photosetId: urlParams.id
		},
		params: {
			response_type: GET_PHOTOSET_IMAGES_LIST_RESPONSE_TYPE,
			is_final_select: true
		}
	})

	return (
		<>
			<PageHero
				endSlot={<StepHeroControls hideSortControls />}
				subtitle={t('photoset:vetting_qa.subtitle')}
				title={t('photoset:vetting_qa.title')}
			/>

			{showConfirmationModal && (
				<VettingQaConfirmationModal
					onClose={() => setShowConfirmationModal(false)}
					photosetId={urlParams.id}
				/>
			)}

			<PhotosetStepWrapper>
				<PhotosetStepBodyContainer>
					{isFetchingManualRetouchingSpaces &&
						[0, 1, 2].map((e) => (
							<PhotosetSpaceElement asSkeleton key={`${e}-id`} />
						))}
					{!(
						isFetchingManualRetouchingSpaces ||
						isErrorManualRetouchingSpaces
					) &&
						photosetSpaces?.map(
							(photosetSpace) =>
								photosetSpace.images.length > 0 && (
									<PhotosetSpaceElement
										endSlot={
											<VettingQaSpaceImages
												images={photosetSpace.images}
												isQaView={
													currentPhotosetSubRoute ===
													'qa'
												}
												photosetId={urlParams.id}
												photosetSpaceId={
													photosetSpace.id
												}
											/>
										}
										key={photosetSpace.space.id}
										spaceName={photosetSpace.space.key_name}
									/>
								)
						)}
				</PhotosetStepBodyContainer>
			</PhotosetStepWrapper>
			{currentPhotosetSubRoute === 'qa' && (
				<PageFooter
					centerSlot={
						<p>{t('photoset:vetting_qa.footer_step_label')}</p>
					}
					endSlot={
						<ButtonLegacy
							onClick={() => setShowConfirmationModal(true)}
							type='button'
						>
							<span>{t('common:retouching_qa')}</span>
							<ArrowRightIcon />
						</ButtonLegacy>
					}
				/>
			)}
		</>
	)
}
